import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { Activity } from '@microsoft/agents-activity'
import { CopilotStudioClient } from '@microsoft/agents-copilotstudio-client'
import { copilotConnectionSettings, loginRequest } from '@/config/authConfig'
import type { ChatMessage } from '@/types/chat'

/**
 * Acquires an access token scoped for the Copilot Studio agent, preferring a
 * silent (cached/refresh) token and falling back to an interactive popup
 * only when required. Tokens are never logged or persisted by this app.
 */
export async function acquireCopilotToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string> {
  try {
    const result = await instance.acquireTokenSilent({ ...loginRequest, account })
    return result.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const result = await instance.acquireTokenPopup(loginRequest)
      return result.accessToken
    }
    throw error
  }
}

export function createCopilotStudioClient(token: string): CopilotStudioClient {
  return new CopilotStudioClient(copilotConnectionSettings, token)
}

export function createOutgoingActivity(text: string): Activity {
  const activity = new Activity('message')
  activity.text = text
  return activity
}

let messageCounter = 0
function nextId(prefix: string): string {
  messageCounter += 1
  return `${prefix}-${Date.now()}-${messageCounter}`
}

/**
 * Maps a Bot Framework Activity coming back from Copilot Studio into the
 * simplified ChatMessage shape used by the UI. Returns null for activities
 * that carry no renderable content (typing indicators, traces, events…).
 */
export function activityToChatMessage(activity: Activity): ChatMessage | null {
  if (activity.type !== 'message') return null
  if (!activity.text || activity.text.trim().length === 0) return null

  const role = activity.from?.role === 'user' ? 'user' : 'agent'
  const timestamp = activity.timestamp ? new Date(activity.timestamp) : new Date()

  return {
    id: activity.id ?? nextId(role),
    role,
    text: activity.text,
    timestamp,
  }
}

export function createLocalMessage(role: ChatMessage['role'], text: string, pending = false): ChatMessage {
  return {
    id: nextId(role),
    role,
    text,
    timestamp: new Date(),
    pending,
  }
}

/**
 * Extracts streaming metadata from an Activity. Copilot Studio emits interim
 * `typing` activities (with accumulated text) and a terminal `message`, both
 * carrying a `streaminfo` entity (or legacy `channelData`) that ties the
 * chunks together by `streamId`.
 */
export function getStreamInfo(activity: Activity): { streamId?: string; final: boolean } {
  const entities = activity.entities as Array<Record<string, unknown>> | undefined
  const info = entities?.find((e) => e?.type === 'streaminfo')
  const channelData = activity.channelData as Record<string, unknown> | undefined

  const streamId = (info?.streamId ?? channelData?.streamId) as string | undefined
  const streamType = (info?.streamType ?? channelData?.streamType) as string | undefined

  return { streamId, final: streamType === 'final' }
}
